import { Button, TransparentButton } from '@approvalmax/ui';
import { openSplitViewState } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import ImageAttention from 'modules/components/ImageIcons/ImageAttention';
import { backend, domain } from 'modules/data';
import { font } from 'modules/styles';
import { updateXeroBPDetails } from 'pages/requestForm/actions';
import { RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { LineItemsTable } from 'pages/requestList/components/lineItemsSection/LineItemsTable';
import { FC, memo, useCallback, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { splitViewGoToBarDefaultSettings, splitViewGoToBarSettingsState } from 'shared/components';
import { useGetBatchPaymentsRequestAmounts } from 'shared/data/webApp/v2';
import { activeAttachmentIdState, ContentSplitViewMode, contentSplitViewState } from 'shared/states';
import styled from 'styled-components';

import { useXeroBillBatchPaymentRequestContext } from '../../XeroBillBatchPaymentRequestContext';
import { messages } from './BillsInformation.messages';
import { getColumnDefinitions, StyledActionMenu } from './tableConfig';

const Root = styled.div`
    height: 92%;
    display: flex;
    flex-direction: column;
    padding: 32px 60px;
`;

const Line = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 25px 0 15px;
`;

const BatchPaymentBills = styled.span`
    text-transform: uppercase;
    ${font(14, '#000', 'semibold')}
`;

const RemoveAllBillsButton = styled(TransparentButton)`
    text-decoration: underline;
    ${font(12, '#a3341a')}

    :disabled {
        color: #a0a1a1;
    }
`;

const TotalContainer = styled.div`
    display: inline-flex;
    flex-direction: column;
    text-align: right;
`;

const TotalText = styled.span`
    ${font(14, '#565656', 'semibold')}
`;

const TotalAmount = styled.span`
    ${font(13, '#000', 'semibold')}
`;

const EmptyState = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const EmptyText = styled.div`
    margin-top: 20px;
    ${font(16, '#000', 'semibold')}
`;

const StyledLineItemsTable = styled(LineItemsTable)`
    && {
        overflow: initial;
        overflow-y: initial;
    }

    tbody tr:hover {
        ${StyledActionMenu} {
            opacity: 1;
            pointer-events: auto;
        }
    }
`;

const qa = bemFactory.qa('xero-bill-batch-payment-creation-request-bills-information');

interface BillsInformationProps {
    request: domain.XeroBillBatchPaymentRequest;
    countryCode: backend.OrganisationVersion;
    toggleIsOpenAddBillsPopup: (value?: boolean) => void;
}

const BillsInformation: FC<BillsInformationProps> = (props) => {
    const { request, countryCode, toggleIsOpenAddBillsPopup } = props;

    const workflowAllowEditSupplierBankAccount = request.authorRules.some(
        (rule) => rule.fieldSystemPurpose === domain.FieldSystemPurpose.XeroSupplier && rule.allowEditing
    );

    const dispatch = useDispatch();

    const {
        billBatchPayment,
        clearItems,
        removeSelectableBillById,
        changeBillInvoiceItemField,
        showErrors,
        billInvoiceRequestIdsWithError,
        billInvoiceRequestIdsWithExceededAmount,
        companyId,
        editMode,
        removeLinesDisabled,
    } = useXeroBillBatchPaymentRequestContext();

    const setOpenSplitView = useSetAtom(openSplitViewState);
    const [contentSplitView, setContentSplitView] = useAtom(contentSplitViewState);
    const setSplitViewGoToBarSettings = useSetAtom(splitViewGoToBarSettingsState);
    const activeAttachmentId = useAtomValue(activeAttachmentIdState);

    const { data: batchPaymentsRequestAmounts, isFetching: isFetchingGetBatchPaymentsRequestAmounts } =
        useGetBatchPaymentsRequestAmounts({
            path: {
                companyId: request.companyId,
                requestId: request.id,
            },
        });

    const onSelectAttachment = useCallback(
        (attachmentId: string, xeroBatchPaymentInvoiceId: string, xeroBatchPaymentInvoiceName: string) => {
            dispatch(updateXeroBPDetails(billBatchPayment));

            setOpenSplitView(true);
            setContentSplitView({
                mode: ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment,
                attachmentId,
                billId: xeroBatchPaymentInvoiceId,
                billFriendlyName: xeroBatchPaymentInvoiceName,
            });
            setSplitViewGoToBarSettings({
                show: true,
                targetContent: {
                    mode: ContentSplitViewMode.BillLinkedToBatchPayment,
                    billId: xeroBatchPaymentInvoiceId,
                },
                targetContentName: xeroBatchPaymentInvoiceName,
            });
        },
        [dispatch, billBatchPayment, setOpenSplitView, setContentSplitView, setSplitViewGoToBarSettings]
    );

    const handleSelectBill = useCallback(
        (xeroBatchPaymentInvoiceId: string) => {
            setContentSplitView({
                mode: ContentSplitViewMode.BillLinkedToBatchPayment,
                billId: xeroBatchPaymentInvoiceId,
            });
            setOpenSplitView(true);
        },
        [setContentSplitView, setOpenSplitView]
    );

    const { items } = billBatchPayment;
    const workflowIsSupplierBankAccountRequired = request.details.workflowIsSupplierBankAccountRequired;

    const handleRemoveSelectableBillById = useCallback(
        (xeroBillInvoiceRequestId: string) => {
            removeSelectableBillById(xeroBillInvoiceRequestId);

            const isBillInSplitView =
                contentSplitView.mode === ContentSplitViewMode.BillLinkedToBatchPayment &&
                contentSplitView.billId === xeroBillInvoiceRequestId;
            const isAttachmentOfBillInSplitView =
                contentSplitView.mode === ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment &&
                contentSplitView.billId === xeroBillInvoiceRequestId;

            if (openSplitViewState && (isBillInSplitView || isAttachmentOfBillInSplitView)) {
                setOpenSplitView(false);
                setContentSplitView({ mode: ContentSplitViewMode.Empty });
                setSplitViewGoToBarSettings(splitViewGoToBarDefaultSettings);
            }
        },
        [contentSplitView, removeSelectableBillById, setContentSplitView, setSplitViewGoToBarSettings, setOpenSplitView]
    );

    const columnDefinitions = useMemo(
        () =>
            getColumnDefinitions({
                removeSelectableBillById: handleRemoveSelectableBillById,
                countryCode,
                changeBillInvoiceItemField,
                showErrors,
                billInvoiceRequestIdsWithError,
                billInvoiceRequestIdsWithExceededAmount,
                onSelectBill: handleSelectBill,
                onSelectAttachment,
                activeAttachmentId,
                currency: request.currency,
                workflowIsSupplierBankAccountRequired,
                workflowAllowEditSupplierBankAccount,
                companyId,
                editMode,
                removeLinesDisabled,
                amounts: batchPaymentsRequestAmounts?.items,
                isAmountsLoading: isFetchingGetBatchPaymentsRequestAmounts,
            }),
        [
            handleRemoveSelectableBillById,
            countryCode,
            changeBillInvoiceItemField,
            showErrors,
            billInvoiceRequestIdsWithError,
            billInvoiceRequestIdsWithExceededAmount,
            handleSelectBill,
            onSelectAttachment,
            activeAttachmentId,
            request.currency,
            workflowIsSupplierBankAccountRequired,
            workflowAllowEditSupplierBankAccount,
            companyId,
            editMode,
            removeLinesDisabled,
            batchPaymentsRequestAmounts,
            isFetchingGetBatchPaymentsRequestAmounts,
        ]
    );

    const totalAmount = useMemo(() => {
        return items.reduce((accum, item) => accum + item.amount, 0);
    }, [items]);

    return (
        <>
            <Root>
                <Line>
                    <BatchPaymentBills>
                        {messages.batchPaymentBills({
                            qty: items.length,
                        })}
                    </BatchPaymentBills>

                    <RemoveAllBillsButton
                        command={{ disabled: editMode === RequestEditMode.Approver }}
                        execute={clearItems}
                        qa={qa('remove-all-bills')}
                    >
                        {messages.removeAllBills}
                    </RemoveAllBillsButton>
                </Line>

                <Line>
                    <Button
                        command={{ disabled: editMode === RequestEditMode.Approver }}
                        execute={toggleIsOpenAddBillsPopup}
                        qa={qa('add-bills')}
                    >
                        {messages.addBills}
                    </Button>

                    <TotalContainer>
                        <TotalText>{messages.total}</TotalText>

                        <TotalAmount>{intl.formatCurrency(totalAmount, request.currency)}</TotalAmount>
                    </TotalContainer>
                </Line>

                {items.length ? (
                    <StyledLineItemsTable columnDefinitions={columnDefinitions} lineItems={items} />
                ) : (
                    <EmptyState>
                        <ImageAttention />

                        <EmptyText>{messages.emptyText}</EmptyText>
                    </EmptyState>
                )}
            </Root>
        </>
    );
};

export default memo(BillsInformation);
